/**
 * ----------------------------------------------------------------------------
 * Nonconformity Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for displaying and managing nonconformity records in a paginated
 * table. Supports default, approved, and verified views, expandable item
 * details, date formatting, refresh functionality, pagination, page-size
 * selection, and role-based approve and verify actions.
 *
 * @author Chandan Singh
 * @created August-2026
 * ----------------------------------------------------------------------------
 */
import { useState, useEffect } from "react"
import Header from "../../shared-components/header/header"
import FormLoader from "../../shared-components/FormLoader/FormLoader"
import Pagination from "../../shared-components/Grid/Pagination"
import { observer } from "mobx-react"
import ListChart from "./component/list-chart"
import ByProcessChart from "./component/by-process-chart"
import SubContractorChart from "./component/sub-contractor-chart"
import ItemChart from "./component/item-chart"
import './non-conformance-report.css'
import { FormattedMessage } from "react-intl"
import { formatMessage } from "../../translations/format-message"
import IPageInfo, { IPageLimitInfo } from "../../models/ICommon"
import { useStore } from "../../contexts/store-provider"
import { INCReportFilter } from "../../models/INonconformanceReportFilter"
import { NCReportFilterInitialState } from "../../core/initialState/filter-initial-state"
import NCReportFilterComponent from "./component/NCReportFilterComponent"
import { ExportHolidayIcon } from "../../shared-components/svgs/svgs"
import { emptyValue } from "../../constants/constants"

type TabKey = "list" | "byProcess" | "subContractor" | "item"

const TABS: { key: TabKey; labelId: string }[] = [
  { key: "list", labelId: "list_view" },
  { key: "byProcess", labelId: "by_process" },
  { key: "subContractor", labelId: "sub_contractor_name" },
  { key: "item", labelId: "item" },
]


function NonconformityReport(props: any) {
  const { nonconformityStore, preferencesStore } = useStore()
  const { userSetting } = preferencesStore

  const {
    GetNonConformanceReportService,
    nonConformanceReportState,
    DownloadNCReportCSV,
    exportNCReportState,
    base64String,
    resetExportNCReportState,
    GetAllProcessService,
    GetSubContractorListService,
    allProcessOptions,
    getSubContractorListOptions,
    GetItemListService,
    getItemListOptions,
    getListViewList,
    listViewList,
    UpdateColumnSelection,
    resetNCReportChartState,
    getColumnsListState,
    NCReportChartState,
    GetNonConformitySummaryService
  } = nonconformityStore
  const [activeTab, setActiveTab] = useState<TabKey>("list")
  const [fromDate, setFromDate] = useState<string>("")
  const [toDate, setToDate] = useState<string>("")

  const [filterValues, setFilterValues] = useState<INCReportFilter>(NCReportFilterInitialState)
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false)

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  })

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  })
  const pageLimitOptions: Array<number> = [10, 20, 50, 100]
  const [disablePagination, setDisablePagination] = useState<boolean>(false);

  const [selected, setSelected] = useState([]);
  const [allowedColumns, setAllowedColumns] = useState(['checkbox']);

  const isLoading = exportNCReportState.inProgress || (activeTab === 'list' ? nonConformanceReportState.inProgress
    : NCReportChartState.inProgress || getColumnsListState.inProgress);

  useEffect(() => {
    const columns = getColumnsList();
    if (activeTab !== 'list' && columns.length > 0) {
      setSelected(columns);
      setAllowedColumns([...columns.map((item) => item.value), 'checkbox']);
    }
  }, [activeTab, allProcessOptions, getSubContractorListOptions, getItemListOptions]);

  // useEffect(() => {
  //   GetNonConformanceReportService(
  //     fromDate,
  //     toDate,
  //     pageInfo.currentPage,
  //     pageLimitInfo.pageLimit,
  //   )
  // }, [pageInfo.currentPage, pageLimitInfo.pageLimit, fromDate, toDate])

  useEffect(() => {
    if (exportNCReportState.success) {
      handleExport(base64String.fileString);
      resetExportNCReportState();
    }
  }, [exportNCReportState.success]);

  useEffect(() => {
    return () => {
      resetNCReportChartState();
    };
  }, []);

  useEffect(() => {
    if(activeTab!=="list")
    GetNonConformitySummaryService(fromDate,toDate)
  },[fromDate,toDate,activeTab])

  // useEffect(() => {
  //   setSelected([]);
  //   if(activeTab === 'byProcess'){
  //     GetAllProcessService()
  //   }else if(activeTab === 'subContractor'){
  //     GetSubContractorListService()
  //   }else if(activeTab === 'item'){
  //     GetItemListService()
  //   }
  // },[activeTab])

  const onPageLimitChanged = (evt: any) => {
    const pageLimit = parseInt(evt.target.value, 10)
    setPageLimit({ pageLimit, isPageLimitChange: true })
    setPageInfo((prev) => ({ ...prev, currentPage: 1 }))
  }

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true })
  }

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false })
  }

  const handleCsvOutput = () => {
    DownloadNCReportCSV(fromDate, toDate)
  }

  const handleExport = (base64Data: string) => {
    // Decode the base64 string
    const binaryData = atob(base64Data);
    const binaryArray = new Uint8Array(binaryData.length);

    // Fill the binary array with the decoded binary data
    for (let i = 0; i < binaryData.length; i++) {
      binaryArray[i] = binaryData.charCodeAt(i);
    }

    // Create a Blob from the binary data with the MIME type for Excel
    const blob = new Blob([binaryArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Create a URL for the Blob and set up the download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = new Date();
    const japanDate = new Date(
      now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })
    );

    const jpString =
      japanDate.getFullYear().toString() +
      String(japanDate.getMonth() + 1).padStart(2, '0') +
      String(japanDate.getDate()).padStart(2, '0') +
      String(japanDate.getHours()).padStart(2, '0') +
      String(japanDate.getMinutes()).padStart(2, '0') +
      String(japanDate.getSeconds()).padStart(2, '0');
    link.download = `Nonconformance-Report-${jpString}.csv`;
    // Trigger the download by simulating a click on the link
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link after download
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    // resetFactoryScheduleDetail();
  };

  const renderChart = () => {
    switch (activeTab) {
      case "list":
        return <ListChart fromDate={fromDate} toDate={toDate} listViewList={getListViewList} pageInfo={pageInfo} pageLimitInfo={pageLimitInfo} />
      case "byProcess":
        return <ByProcessChart fromDate={fromDate} toDate={toDate} />
      case "subContractor":
        return <SubContractorChart fromDate={fromDate} toDate={toDate} />
      case "item":
        return <ItemChart fromDate={fromDate} toDate={toDate} />
      default:
        return null
    }
  }

  const handleFilterSubmit = (values: INCReportFilter) => {
    setFilterValues(values)
    setFromDate(values.StartDate || '')
    setToDate(values.EndDate || '')
    setPageInfo((prev) => ({ ...prev, currentPage: 1 }))
  }

  const handleFilterClear = (values: INCReportFilter, isCleared: boolean) => {
    setFilterValues(NCReportFilterInitialState)
    setFromDate('')
    setToDate('')
    setIsFilterApplied(false)
    setPageInfo((prev) => ({ ...prev, currentPage: 1 }))
    resetNCReportChartState()
  }

  const updateColumnsInStore = (selectedOptions: any[]) => {
    let valuesToStore: (number | string)[] = [];
    if (activeTab === "byProcess") {
      valuesToStore = selectedOptions.map((item) => Number(item.id));
    } else {
      valuesToStore = selectedOptions.map((item) => String(item.value));
    }
    UpdateColumnSelection(valuesToStore);
  };

  const handleSelectionChange = async (selectedOptions) => {
    if (selectedOptions.length === 0) {
      setSelected([]);
      setAllowedColumns([
        // ...initialSelection.map((item) => item.value),
        // 'checkbox',
      ]);
    } else {
      setSelected(selectedOptions);
      setAllowedColumns([
        ...selectedOptions.map((item) => item.value)
      ]);
    }
    updateColumnsInStore(selectedOptions);

    // SaveProcessOrderService({
    //   SettingKey: userSettingEnum.OrderTableColumn,
    //   ProcessOrder: selectedOptions.map((opt) => opt.value).join(','),
    // });
  };

  const getColumnsList = () => {
    switch (activeTab) {
      case "byProcess":
        return allProcessOptions.map((field) => ({
          ...field,
          label: formatMessage(field.value),
        }));
      case "subContractor":
        return getSubContractorListOptions.map((field) => ({
          ...field,
          label: field.value?.length > 0 ? formatMessage(field.value) : emptyValue,
        }));
      case "item":
        return getItemListOptions.map((field) => ({
          ...field,
          label: field.value?.length > 0 ? formatMessage(field.value) : emptyValue,
        }));
      default:
        return [];
    }
  }

  return (
    <>
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center order-table">
          <Header className="col-auto" label="nonconformity_report" />

          <div className="col">
            <ul className="add-list">

              {isFilterApplied && (fromDate || toDate) && (
                <li className="nc-navbar-range">
                  <span className="nc-chart-range">
                    {fromDate || "..."} ~ {toDate || "..."}
                  </span>
                </li>
              )}
              <li>
                <NCReportFilterComponent
                  id={1}
                  initialValues={filterValues}
                  submitHandler={handleFilterSubmit}
                  filterParmas={handleFilterClear}
                  onModalClosed={() => { }}
                  isLoading={false}
                  isDisable={false}
                  isFilterCleared={!isFilterApplied}
                  isFilterApplied={isFilterApplied}
                  setIsFilterApplied={setIsFilterApplied}
                />
              </li>
              <ul className="tableAccordian-switch d-flex">
                {TABS.map((tab) => (
                  <li
                    key={tab.key}
                    className={activeTab === tab.key ? 'active' : ''}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className="icon-text">
                      <FormattedMessage id={tab.labelId} />
                    </span>
                  </li>
                ))}
              </ul>

              <li title={formatMessage('export_csv')}>
                <a
                  className={`btn btn-light ${exportNCReportState.inProgress ? 'disabled' : ''}`}
                  onClick={exportNCReportState.inProgress ? undefined : handleCsvOutput}
                >
                  <ExportHolidayIcon />
                </a>
              </li>


            </ul>
          </div>
        </div>
      </div>

      <FormLoader loading={isLoading} />

      <div className="card card-page">
        <div className="changingPanel">
          {renderChart()}
        </div>

        {activeTab === 'list' && (
          <div className="row no-gutters p-3 pt-0 tableFooter">
            <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
              <FormattedMessage id="show" />
              <select
                data-testid="pageSelect"
                className="pageLimit"
                onChange={onPageLimitChanged}
                value={pageLimitInfo.pageLimit}
              >
                {pageLimitOptions.map((op: any) => (
                  <option
                    key={op}
                    value={op}
                  >
                    {op}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
              <Pagination
                isRefreshPage={false}
                totalPages={pageInfo.totalPages}
                pageLimit={pageLimitInfo.pageLimit}
                onPageChanged={onPageChanged}
                disablePagination={disablePagination}
                setIsRefreshPage={setIsRefreshPage}
                currentPage={pageInfo.currentPage}
                totalRecords={listViewList?.PagingDetails?.TotalRecords}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default observer(NonconformityReport)
